import { useEffect, useState } from "react";
import api from "../../lib/api";
import { Send, Users, History, Mail, Loader2 } from "lucide-react";
import { toast } from "sonner";

const FILTERS = [
  { v: "all", label: "All users" },
  { v: "basic", label: "Basic plan" },
  { v: "standard", label: "Standard plan" },
  { v: "premium", label: "Premium plan" },
  { v: "trialing", label: "Trialing (in 3-day trial)" },
  { v: "inactive", label: "Inactive (no plan / cancelled)" },
];

const TEMPLATES = [
  {
    name: "Welcome",
    subject: "Welcome to Djino Stream 🎬",
    html: "<p>Hi {{name}},</p><p>Welcome aboard! Your Djino Stream account is ready. Pick a profile, queue up your first watchlist, and dive into thousands of films, series and live channels.</p>",
  },
  {
    name: "New content drop",
    subject: "New on Djino this week",
    html: "<p>Hey {{name}},</p><p>We just dropped fresh originals — including a brand new Congolese drama and three African films. Tap below to start watching.</p>",
  },
  {
    name: "Trial ending soon",
    subject: "Your free trial ends in 24 hours",
    html: "<p>{{name}}, just a heads-up — your 3-day free trial ends tomorrow. To keep streaming, no action needed. To cancel, head to Subscriptions before the trial ends.</p>",
  },
];

export default function AdminEmail() {
  const [filter, setFilter] = useState("all");
  const [subject, setSubject] = useState("");
  const [html, setHtml] = useState("");
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState([]);
  const [audienceSize, setAudienceSize] = useState(null);

  const loadHistory = () => api.get("/admin/email/history").then((r) => setHistory(r.data));

  useEffect(() => {
    loadHistory();
    api.get("/admin/users").then((r) => {
      setAudienceSize(estimateSize(r.data, filter));
    });
  }, [filter]);

  const send = async () => {
    if (!subject.trim() || !html.trim()) return toast.error("Subject and message required");
    if (!window.confirm(`Send to ~${audienceSize ?? "?"} users? This cannot be undone.`)) return;
    setSending(true);
    try {
      const { data } = await api.post("/admin/email/broadcast", { subject, html, filter });
      toast.success(`Sent to ${data.sent} of ${data.recipients} users${data.failed ? ` (${data.failed} failed)` : ""}`);
      setSubject("");
      setHtml("");
      loadHistory();
    } catch (e) {
      toast.error("Broadcast failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setSending(false);
    }
  };

  const applyTemplate = (t) => {
    setSubject(t.subject);
    setHtml(t.html);
  };

  return (
    <div data-testid="admin-email-page">
      <div className="mb-8">
        <span className="text-xs font-bold text-[#E50914] uppercase tracking-[0.3em]">Communications</span>
        <h1 className="text-4xl font-black tracking-tighter" style={{ fontFamily: "Outfit" }}>Bulk Email</h1>
        <p className="text-[#888] text-sm mt-1">Send an announcement, promo, or trial reminder to a slice of your user base.</p>
      </div>

      <div className="grid lg:grid-cols-[1fr_320px] gap-6">
        <div className="bg-[#0d0d0d] border border-white/5 rounded-2xl p-6">
          <div className="mb-4">
            <label className="text-xs uppercase tracking-wider text-[#888] block mb-1.5">Audience</label>
            <select value={filter} onChange={(e) => setFilter(e.target.value)} className="w-full px-3 py-2.5 bg-[#1a1a1a] border border-white/10 rounded-md text-sm focus:outline-none focus:border-[#E50914]" data-testid="email-filter">
              {FILTERS.map((f) => <option key={f.v} value={f.v}>{f.label}</option>)}
            </select>
            <div className="mt-2 text-xs text-[#888] flex items-center gap-2">
              <Users size={12} /> ~{audienceSize ?? "?"} recipients
            </div>
          </div>

          <div className="mb-4">
            <label className="text-xs uppercase tracking-wider text-[#888] block mb-1.5">Subject</label>
            <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Your subject..." className="w-full px-3 py-2.5 bg-[#1a1a1a] border border-white/10 rounded-md text-sm focus:outline-none focus:border-[#E50914]" data-testid="email-subject" />
          </div>

          <div className="mb-4">
            <label className="text-xs uppercase tracking-wider text-[#888] block mb-1.5">Message (HTML supported, <code>{"{{name}}"}</code> is replaced with recipient name)</label>
            <textarea rows="10" value={html} onChange={(e) => setHtml(e.target.value)} placeholder="<p>Hi {{name}},</p><p>Big news at Djino...</p>" className="w-full px-3 py-2.5 bg-[#1a1a1a] border border-white/10 rounded-md text-sm font-mono focus:outline-none focus:border-[#E50914]" data-testid="email-body" />
          </div>

          <button onClick={send} disabled={sending} className="btn-primary" data-testid="email-send-btn">
            {sending ? <><Loader2 className="animate-spin" size={16} /> Sending...</> : <><Send size={16} /> Send broadcast</>}
          </button>
        </div>

        <aside className="space-y-5">
          <div className="bg-[#0d0d0d] border border-white/5 rounded-2xl p-5">
            <div className="flex items-center gap-2 mb-3">
              <Mail size={16} className="text-[#E50914]" />
              <h3 className="text-sm font-bold uppercase tracking-wider" style={{ fontFamily: "Outfit" }}>Templates</h3>
            </div>
            <div className="space-y-2">
              {TEMPLATES.map((t) => (
                <button key={t.name} onClick={() => applyTemplate(t)} className="w-full text-left p-3 bg-[#0a0a0a] hover:bg-[#161616] rounded-md border border-white/5 text-sm transition" data-testid={`template-${t.name}`}>
                  <div className="font-semibold mb-0.5">{t.name}</div>
                  <div className="text-xs text-[#888] line-clamp-1">{t.subject}</div>
                </button>
              ))}
            </div>
          </div>

          <div className="bg-[#0d0d0d] border border-white/5 rounded-2xl p-5">
            <div className="flex items-center gap-2 mb-3">
              <History size={16} className="text-[#E50914]" />
              <h3 className="text-sm font-bold uppercase tracking-wider" style={{ fontFamily: "Outfit" }}>Recent broadcasts</h3>
            </div>
            {history.length === 0 ? (
              <div className="text-xs text-[#666] py-3">No broadcasts sent yet.</div>
            ) : (
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {history.map((h) => (
                  <div key={h.id} className="p-3 bg-[#0a0a0a] rounded-md border border-white/5 text-xs">
                    <div className="font-semibold text-sm text-white truncate">{h.subject}</div>
                    <div className="text-[#888] mt-1">{new Date(h.created_at).toLocaleString()}</div>
                    <div className="flex items-center gap-3 mt-1.5 text-[#bbb]">
                      <span>→ {h.recipients} {h.filter !== "all" ? `(${h.filter})` : ""}</span>
                      <span className="text-[#00C853]">✓ {h.sent}</span>
                      {h.failed > 0 && <span className="text-[#E50914]">✗ {h.failed}</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}

function estimateSize(users, filter) {
  if (filter === "all") return users.length;
  if (["basic", "standard", "premium"].includes(filter)) {
    return users.filter((u) => u.subscription_plan === filter && u.subscription_status === "active").length;
  }
  if (filter === "trialing") return users.filter((u) => u.subscription_status === "trialing").length;
  if (filter === "inactive") return users.filter((u) => !u.subscription_status || ["inactive", "cancelled"].includes(u.subscription_status)).length;
  return users.length;
}
